import { computed, ref } from 'vue'
import { useCookie, useRuntimeConfig } from '#app'
import type { Tenant } from '../types'

const tenants = ref<Tenant[]>([])
const selectedTenantId = ref<string | undefined>()
const checking = ref(false)
const tenantsFetched = ref(false)

export default function useTenant() {
	const config = useRuntimeConfig().public

	const cookieDomain = (config.tenant as { cookieDomain?: string }).cookieDomain || undefined

	// Cookie stores only the tenant ID — never the full object.
	// This prevents stale baseUrl/realm data from being used as a fallback.
	// domain muss gesetzt sein, damit der Cookie auch auf Subdomains (z.B. demo.verein-checkin.de) lesbar ist.
	const lastTenantId = useCookie<string | null>('vc-last-tenant', {
		maxAge: 60 * 60 * 24 * 365, // 1 year
		path: '/',
		sameSite: 'lax',
		...(cookieDomain ? { domain: cookieDomain } : {}),
	})

	const resolvedTenant = computed<Tenant | undefined>(() => {
		const id = selectedTenantId.value ?? lastTenantId.value
		if (id) {
			return tenants.value.find(t => t.id === id)
		}
		return undefined
	})

	if (!selectedTenantId.value && lastTenantId.value) {
		selectedTenantId.value = lastTenantId.value
	}

	async function fetchTenants() {
		const tenantsUrl = (config.tenant as { tenantsUrl: string }).tenantsUrl
		tenants.value = await $fetch<Tenant[]>(`${tenantsUrl}/api/tenants`)
		tenantsFetched.value = true
	}

	function setLastTenant(tenant: Tenant) {
		lastTenantId.value = tenant.id
	}

	/**
	 * Löst den Tenant anhand der aktuellen Browser-Origin auf.
	 * Subdomain hat Vorrang vor dem Cookie — bei Abweichung wird der Cookie aktualisiert.
	 */
	function resolveFromOrigin(origin: string) {
		const matches = tenants.value.filter((t) => {
			try {
				return new URL(t.baseUrl).origin === origin
			}
			catch {
				return false
			}
		})

		// Eindeutiger Match → Subdomain hat Vorrang über Cookie
		if (matches.length === 1) {
			selectedTenantId.value = matches[0].id
			lastTenantId.value = matches[0].id
		}
		// Mehrere Tenants auf gleicher Origin (z.B. lokal) → Cookie-Fallback beibehalten
		// selectedTenantId bleibt auf lastTenantId (aus Cookie), gesetzt im Constructor
	}

	return {
		tenants,
		selectedTenantId,
		resolvedTenant,
		tenantsFetched,
		checking,
		fetchTenants,
		setLastTenant,
		resolveFromOrigin,
	}
}
